/*
 * Course Detail page (Course Drill Down).
 *
 * Single-course deep-dive mirroring the Sim Detail page: header with name /
 * category, headline KPIs (sent, completed, outstanding, avg score), and a
 * recipients table aligned with the Course Send History columns.
 *
 * Calculations: aggregates COURSE_SEND_LOG entries where courseName matches;
 * prefers the server 'courses' aggregate row for sent/completed when present.
 * Avg score = mean of correctAnswers/totalQuestions over scored attempts.
 *
 * Postgres mapping:
 *   SELECT course_id, COUNT(*), COUNT(completed_at) FROM course_sends
 *   WHERE course_id = ? ; recipients = same rows joined to users.
 *
 * Integration hook: `useCourseDetailQuery(courseName, { period, scope })`
 * cache 30 min.
 */
import React, { useState, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import { COURSES, COURSE_SEND_LOG, DATA_EPOCH } from '../data';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { toServerScope, userScopeIsEmails } from '../serverScope';
import DataTable from './DataTable';
import { FilterMulti } from './SendHistoryPage';
import { courseLogColumns, courseStatus, courseOutcome, courseScore, applyScopeToCourseLog, COURSE_STATUS_OPTIONS, OUTCOME_OPTIONS } from './CourseSendHistoryPage';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function MetricCard({ label, value, unit, sub, color }) {
    return (
        <motion.div variants={fadeUp} style={{ height: '100%', width: '100%', display: 'flex' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: color || 'text.primary' }}>
                            <CountUp end={parseFloat(String(value).replace(/[^0-9.]/g, ''))} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} />
                        </Typography>
                        {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                    </Box>
                    {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
                </Box>
            </MainCard>
        </motion.div>
    );
}

function courseKpis(aggRow, events) {
    const totalSent = aggRow ? aggRow.sent : events.length;
    const completed = aggRow ? aggRow.completed : events.filter((e) => e.dateCompleted).length;
    const scored = events.map((e) => courseScore(e)).filter((s) => s != null);
    return {
        totalSent,
        completed,
        outstanding: Math.max(0, totalSent - completed),
        scoredCount: scored.length,
        avgScore: scored.length ? Math.round(scored.reduce((a, s) => a + s, 0) / scored.length) : 0
    };
}

export default function CourseDetailPage({ go, drill, courseName, period, startDate, endDate, scope }) {
    const course = COURSES.find((c) => c.name === courseName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allCourseEvents = useMemo(() => COURSE_SEND_LOG.filter((e) => e.courseName === courseName), [courseName, DATA_EPOCH]);
    // A carried-over course filter could only empty a single-course page (BD-1169).
    const peopleScope = useMemo(() => ({ ...scope, course: undefined }), [scope]);
    // KPIs and the recipients table (and its export) follow the selected period (BD-1003).
    const courseEvents = useMemo(
        () => applyScopeToCourseLog(logInPeriod(allCourseEvents, { period, startDate, endDate }), peopleScope),
        [allCourseEvents, period, startDate, endDate, peopleScope]
    );
    const coursesQ = useDashboardData(
        { metric: 'courses', aggregation: 'courses', period, startDate, endDate, scope: toServerScope(scope) },
        { skip: !userScopeIsEmails(scope) }
    );
    const aggRow = Array.isArray(coursesQ.data?.courses) ? coursesQ.data.courses.find((r) => r.courseName === courseName) : null;

    // Recipients table: the canonical course-send columns (minus the Course
    // column - every row here is this course), same filters as the history page.
    const recipientColumns = useMemo(() => courseLogColumns({ includeCourse: false }), []);
    const [filterUsers, setFilterUsers] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);
    const [filterLocations, setFilterLocations] = useState([]);
    const [filterStatuses, setFilterStatuses] = useState([]);
    const [filterOutcomes, setFilterOutcomes] = useState([]);
    const recipientUsers = useMemo(() => {
        const m = new Map();
        for (const e of courseEvents) if (!m.has(e.email)) m.set(e.email, `${e.firstName} ${e.lastName}`.trim() || e.email);
        return [...m.entries()].map(([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [courseEvents]);
    const recipientDepts = useMemo(() => [...new Set(courseEvents.map((e) => e.dept))].sort(), [courseEvents]);
    const recipientLocations = useMemo(() => [...new Set(courseEvents.map((e) => e.location))].sort(), [courseEvents]);
    const recipientRows = useMemo(() => courseEvents.filter((e) => {
        if (filterUsers.length     && !filterUsers.some((u) => u.email === e.email)) return false;
        if (filterDepts.length     && !filterDepts.includes(e.dept))                 return false;
        if (filterLocations.length && !filterLocations.includes(e.location))         return false;
        if (filterStatuses.length  && !filterStatuses.includes(courseStatus(e)))     return false;
        if (filterOutcomes.length  && !filterOutcomes.includes(courseOutcome(e)))    return false;
        return true;
    }), [courseEvents, filterUsers, filterDepts, filterLocations, filterStatuses, filterOutcomes]);

    if (!allCourseEvents.length && !course) {
        return (
            <Box sx={{ p: '24px', color: 'text.disabled', fontSize: 13 }}>Course not found.</Box>
        );
    }

    const { totalSent, completed, outstanding, scoredCount, avgScore } = courseKpis(aggRow, courseEvents);
    const type = course?.type || allCourseEvents[0]?.courseType;

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>{courseName}</Typography>
                    {type && (
                        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
                            {type}
                        </Box>
                    )}
                </Box>
            </motion.div>

            <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                <Grid item xs={6} md={3}><MetricCard label="Total Sent" value={totalSent} sub={period ? `${period} window` : 'Selected window'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Completed" value={completed} color="#66BB6A" sub={totalSent ? `${Math.round((completed / totalSent) * 100)}% completion` : 'No sends in window'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Outstanding" value={outstanding} color="#FFCA28" sub="Not yet completed" /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Avg Score" value={avgScore} unit="%" sub={`Across ${scoredCount.toLocaleString()} scored attempts`} /></Grid>
            </Grid>

            <motion.div variants={fadeUp}>
                <DataTable
                    title="Recipients"
                    columns={recipientColumns}
                    rows={recipientRows}
                    rowKey={(e) => `${e.email}-${e.sentAt || e.dateSent}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-course-recipients-cols"
                    emptyMessage="No send log entries for this course"
                    toolbar={
                        <>
                            <FilterMulti value={filterUsers}     onChange={setFilterUsers}     options={recipientUsers} placeholder="All users" getLabel={(u) => `${u.name} (${u.email})`} />
                            <FilterMulti value={filterDepts}     onChange={setFilterDepts}     options={recipientDepts} placeholder="All departments" />
                            <FilterMulti value={filterLocations} onChange={setFilterLocations} options={recipientLocations} placeholder="All locations" />
                            <FilterMulti value={filterStatuses}  onChange={setFilterStatuses}  options={COURSE_STATUS_OPTIONS} placeholder="All statuses" />
                            <FilterMulti value={filterOutcomes}  onChange={setFilterOutcomes}  options={OUTCOME_OPTIONS} placeholder="All outcomes" />
                        </>
                    }
                />
            </motion.div>

        </motion.div>
    );
}
